import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Crown, Loader2, AlertCircle, Receipt, FileDown, ExternalLink,
} from "lucide-react";
import PlanCard from "@/components/billing/PlanCard";
import ExtraQuoteCard from "@/components/billing/ExtraQuoteCard";
import RefundNotice from "@/components/billing/RefundNotice";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { errorCode, errorMessage, gateErrorText } from "@/lib/errors";
import { PROMO_STORAGE_KEY } from "@/pages/Promo";
import { formatDate } from "@/lib/dates";

const POLL_MS = 2000;
const MAX_POLL_ATTEMPTS = 10;

export default function BillingPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  // Both from /billing/config (platform_config): the price of one extra quote
  // and how many one purchase may buy. No literal fallbacks — a typed number
  // would be shown, or clamped to, whenever the request failed.
  const [extraPrice, setExtraPrice] = useState(null);
  const [extraMax, setExtraMax] = useState(1);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // The live subscription row. The page used to derive everything from
  // `profile.tier_priority`, which cannot tell a plan that was paid for from
  // one FixyDany granted — and only the second kind has nothing to cancel.
  const [subscription, setSubscription] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);

  const profile = user?.profile || {};
  const currentTier = profile.tier_priority ?? 0;

  const loadAll = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const [cfgRes, histRes, invRes, subRes] = await Promise.all([
        api.get("/billing/config"),
        api.get("/billing/history?limit=20").catch(() => ({ data: { payments: [] } })),
        api.get("/billing/invoices?limit=24").catch(() => ({ data: { invoices: [] } })),
        api.get("/billing/subscription").catch(() => ({ data: { subscription: null } })),
      ]);
      setPlans(cfgRes.data.plans || []);
      setExtraPrice(cfgRes.data.extra_credit_price_usd ?? null);
      setExtraMax(cfgRes.data.extra_credit_max_per_purchase || 1);
      setHistory(histRes.data.payments || []);
      setInvoices(invRes.data.invoices || []);
      setSubscription(subRes.data.subscription || null);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Stripe redirect handling: poll session status until finalized
  useEffect(() => {
    const sessionId = params.get("session_id");
    const success = params.get("success");
    const canceled = params.get("canceled");

    if (canceled === "true") {
      setPollStatus({ type: "warning", message: t("billingX.checkoutCanceled") });
      const next = new URLSearchParams(params);
      next.delete("canceled");
      setParams(next, { replace: true });
      // Stripe leaves a cancelled Checkout open for a day; closing it is what
      // keeps it out of the history as PENDING. Best effort: the history
      // reconciles anyway once Stripe expires it.
      api.post("/billing/checkout/abandon").catch(() => {}).finally(() => loadAll());
      return;
    }

    if (success === "true" && sessionId) {
      let attempts = 0;
      let done = false;
      setPollStatus({ type: "info", message: t("billingX.confirmingPayment") });
      const poll = async () => {
        if (done) return;
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/session/${sessionId}`);
          if (data.status === "paid") {
            done = true;
            setPollStatus({ type: "success", message: t("billingX.paymentConfirmed") });
            await refresh();
            await loadAll();
            const next = new URLSearchParams(params);
            next.delete("success"); next.delete("session_id");
            setParams(next, { replace: true });
            return;
          }
          if (data.status === "canceled") {
            done = true;
            setPollStatus({ type: "warning", message: t("billingX.sessionExpired") });
            return;
          }
        } catch {
          // ignore transient
        }
        if (attempts < MAX_POLL_ATTEMPTS) {
          setTimeout(poll, POLL_MS);
        } else {
          setPollStatus({ type: "info", message: t("billingX.stillProcessing") });
        }
      };
      poll();
    }
  }, [params, setParams, refresh, loadAll, t]);

  const startCheckout = async (planKey, creditsQuantity = 1) => {
    setPendingPlan(planKey);
    try {
      // A courtesy code the visitor arrived with, parked by /promo/:code so it
      // survives registration and onboarding. Subscriptions only — the server
      // ignores it on credits, and sending it there would be misleading.
      let promotionCode = null;
      if (planKey !== "credits") {
        try { promotionCode = sessionStorage.getItem(PROMO_STORAGE_KEY); } catch { /* private mode */ }
      }
      const { data } = await api.post("/billing/checkout", {
        plan: planKey,
        credits_quantity: creditsQuantity,
        origin_url: window.location.origin,
        promotion_code: promotionCode || null,
      });
      if (data.promotion_declined_reason) {
        // Spent already, on this card or this phone. Say so before Stripe shows
        // a full-price page the user was not expecting.
        setPollStatus({
          type: "error",
          message: t(`billingX.promoDeclined.${data.promotion_declined_reason}`, {
            defaultValue: t("billingX.promoDeclined.invalid"),
          }),
        });
        setPendingPlan(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (data.promotion_applied) {
        try { sessionStorage.removeItem(PROMO_STORAGE_KEY); } catch { /* private mode */ }
      }
      if (data.url) window.location.href = data.url;
      else {
        setPollStatus({ type: "error", message: t("billingX.couldNotStart") });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      // A 100% courtesy code needs a confirmed phone (2026-10-28): the banner
      // links to the page that confirms it, and the code stays parked for the
      // next try.
      const phoneGate = errorCode(err) === "phone_verification_required";
      const msg = phoneGate ? t("signupX.gateCourtesy") : gateErrorText(err, t, t("billingX.checkoutFailed"));
      setPollStatus({ type: "error", phoneGate,
                      message: typeof msg === "string" ? msg : t("billingX.checkoutFailed") });
      setPendingPlan(null);
      // The status banner lives at the top of the page; bring it into view so a
      // failed purchase doesn't look like a dead button.
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const cancelSub = async () => {
    if (!window.confirm(t("billingX.confirmCancel"))) return;
    try {
      await api.post("/billing/cancel");
      setPollStatus({ type: "success", message: t("billingX.cancelScheduled") });
      await loadAll();
      await refresh();
    } catch (err) {
      setPollStatus({ type: "error", message: errorMessage(err, t("billingX.cancelFailed")) });
    }
  };

  return (
    <DashboardLayout role="provider">
      <div data-testid="billing-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
              {t("billingX.title")}
            </h1>
            <p className="mt-2 text-smoke max-w-xl">
              {t("billingX.subtitle")}
            </p>
          </div>
          <div className="badge badge-verified flex items-center gap-1.5">
            <Crown size={12} /> {t("billingX.current")} {["Starter", "Pro", "Master"][currentTier] || "Starter"}
          </div>
        </div>

        {pollStatus && (
          <div
            className={`mb-6 p-3 rounded-md border flex items-start gap-2 text-sm font-display ${
              pollStatus.type === "success" ? "bg-green-50 border-green-300 text-green-800" :
              pollStatus.type === "error" ? "bg-red-50 border-red-300 text-red-800" :
              pollStatus.type === "warning" ? "bg-amber-50 border-amber-300 text-amber-800" :
              "bg-linen border-sand text-worker"
            }`}
            data-testid="billing-status"
          >
            {pollStatus.type === "info" && <Loader2 size={16} className="animate-spin mt-0.5" />}
            {pollStatus.type !== "info" && <AlertCircle size={16} className="mt-0.5" />}
            <span>
              {pollStatus.message}
              {pollStatus.phoneGate && (
                <>
                  {" "}
                  <Link to="/provider/verify-phone" className="font-semibold underline" data-testid="billing-phone-gate-cta">
                    {t("signupX.gateCta")}
                  </Link>
                </>
              )}
            </span>
          </div>
        )}

        {/* Plans grid — three identical cards (PLANES M4a) */}
        <div className="grid md:grid-cols-3 gap-4 mb-6 items-stretch">
          {loadingPlans ? (
            <div className="md:col-span-3 text-center text-smoke py-12">
              <Loader2 className="animate-spin inline mr-2" /> {t("billingX.loading")}
            </div>
          ) : plans.map((p) => {
            const isCurrent = currentTier === p.tier;
            const canSelect = !isCurrent && p.tier > 0;
            const planKey = p.tier === 1 ? "pro" : p.tier === 2 ? "master" : null;
            let footer;
            if (canSelect) {
              footer = (
                <div className="space-y-2">
                  <button
                    onClick={() => startCheckout(planKey)}
                    disabled={pendingPlan === planKey}
                    className="fx-btn fx-btn--primary w-full"
                    data-testid={`plan-checkout-${planKey}`}
                  >
                    {pendingPlan === planKey ? (
                      <><Loader2 className="animate-spin" size={16} /> {t("billingX.redirecting")}</>
                    ) : (
                      <>{t("billingX.upgradeTo", { name: p.name })}</>
                    )}
                  </button>
                  <RefundNotice testId={`plan-refund-notice-${p.plan_id}`} />
                </div>
              );
            } else if (isCurrent && p.tier > 0 && subscription?.cancellable && !subscription?.cancel_at_period_end) {
              footer = (
                <button
                  onClick={cancelSub}
                  className="fx-btn fx-btn--secondary w-full text-sm"
                  data-testid={`plan-cancel-${p.plan_id}`}
                >
                  {t("billingX.cancelAtEnd")}
                </button>
              );
            } else if (isCurrent && p.tier > 0 && subscription?.is_manual) {
              /* A courtesy plan has no Stripe subscription behind it, so
                 POST /billing/cancel answers 400 and there is nothing for the
                 button to do. Say what the plan is instead. */
              footer = (
                <div className="text-center" data-testid={`plan-complimentary-${p.plan_id}`}>
                  <p className="text-xs font-display font-semibold text-worker">
                    {t("billingX.complimentary")}
                  </p>
                  <p className="mt-1 text-[11px] text-smoke">
                    {t("billingX.complimentaryNote")}
                  </p>
                </div>
              );
            } else if (isCurrent && p.tier > 0 && subscription?.cancel_at_period_end) {
              footer = (
                <p className="text-center text-xs text-smoke" data-testid={`plan-cancel-pending-${p.plan_id}`}>
                  {t("billingX.cancelScheduledBadge")}
                </p>
              );
            } else {
              footer = (
                <p className="text-center text-xs text-smoke italic">
                  {p.tier === 0 ? t("billingX.defaultPlan") : ""}
                </p>
              );
            }
            return (
              <PlanCard
                key={p.plan_id}
                plan={p}
                badge={isCurrent ? (
                  <span className="badge badge-verified text-[10px]" data-testid={`plan-current-${p.plan_id}`}>
                    {t("common.active")}
                  </span>
                ) : null}
                footer={footer}
              />
            );
          })}
        </div>

        {/* An extra quote, its own card (PLANES M4b) */}
        {extraPrice != null && (
          <div className="mb-12">
            <ExtraQuoteCard
              mode="buy"
              price={extraPrice}
              max={extraMax}
              plans={plans}
              pending={pendingPlan === "credits"}
              onBuy={(qty) => startCheckout("credits", qty)}
            />
          </div>
        )}

        {/* History */}
        <div className="card-base p-6" data-testid="billing-history">
          <div className="flex items-center gap-2 mb-4">
            <Receipt size={20} className="text-smoke" />
            <h3 className="font-display text-xl font-bold text-worker">{t("billingX.recentPayments")}</h3>
          </div>
          {history.length === 0 ? (
            <p className="text-smoke text-sm">{t("billingX.noPayments")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-smoke font-display border-b border-sand">
                    <th className="py-2 pr-4">{t("billingX.thDate")}</th>
                    <th className="py-2 pr-4">{t("billingX.thPurpose")}</th>
                    <th className="py-2 pr-4">{t("billingX.thAmount")}</th>
                    <th className="py-2">{t("billingX.thStatus")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.session_id} className="border-b border-sand/60 last:border-0" data-testid={`payment-${h.session_id}`}>
                      <td className="py-3 pr-4 font-mono text-smoke">
                        {formatDate(h.created_at)}
                      </td>
                      <td className="py-3 pr-4 font-display capitalize">
                        {h.purpose === "subscription" ? (h.metadata?.target_plan === "plan_master" ? t("billingX.purposeMaster") : t("billingX.purposePro")) : t("billingX.purposeCredits")}
                      </td>
                      <td className="py-3 pr-4 font-mono">
                        {h.amount_total ? `$${(h.amount_total / 100).toFixed(2)}` : "—"}
                      </td>
                      <td className="py-3">
                        <span className={`badge ${h.status === "paid" ? "badge-verified" : ""}`}
                              data-testid={`payment-status-${h.session_id}`}>
                          {t(`adminUser.payState.${h.status}`, { defaultValue: h.status })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stripe Invoices */}
        <div className="card-base p-6 mt-6" data-testid="billing-invoices">
          <div className="flex items-center gap-2 mb-4">
            <FileDown size={20} className="text-smoke" />
            <h3 className="font-display text-xl font-bold text-worker">{t("billingX.invoicesTitle")}</h3>
            <span className="text-xs text-smoke ml-auto">{t("billingX.poweredStripe")}</span>
          </div>
          {invoices.length === 0 ? (
            <p className="text-smoke text-sm">{t("billingX.noInvoices")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-smoke font-display border-b border-sand">
                    <th className="py-2 pr-4">{t("billingX.thNumber")}</th>
                    <th className="py-2 pr-4">{t("billingX.thDate")}</th>
                    <th className="py-2 pr-4">{t("billingX.thAmount")}</th>
                    <th className="py-2 pr-4">{t("billingX.thStatus")}</th>
                    <th className="py-2">{t("billingX.thActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-sand/60 last:border-0" data-testid={`invoice-${inv.id}`}>
                      <td className="py-3 pr-4 font-mono text-worker">
                        {inv.number || inv.id.slice(0, 14)}
                      </td>
                      <td className="py-3 pr-4 font-mono text-smoke">
                        {(inv.created && formatDate(inv.created * 1000)) || "—"}
                      </td>
                      <td className="py-3 pr-4 font-mono">
                        {inv.amount_paid ? `$${(inv.amount_paid / 100).toFixed(2)} ${inv.currency}` : "—"}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`badge ${inv.status === "paid" ? "badge-verified" : ""}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 flex gap-2">
                        {inv.invoice_pdf && (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fx-btn fx-btn--secondary text-xs py-1 px-2"
                            data-testid={`invoice-pdf-${inv.id}`}
                            title={t("billingX.downloadPdf")}
                          >
                            <FileDown size={12} /> {t("billingX.pdf")}
                          </a>
                        )}
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fx-btn fx-btn--ghost text-xs py-1 px-2"
                            data-testid={`invoice-view-${inv.id}`}
                            title={t("billingX.viewHosted")}
                          >
                            <ExternalLink size={12} /> {t("billingX.view")}
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
